import { Component, OnInit, ChangeDetectorRef, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroup } from '@angular/forms';

import { Store } from '@ngrx/store';

import { combineLatest, merge, Subject, Observable } from 'rxjs';
import { takeUntil, filter, map } from 'rxjs/operators';

import { AppState, Selectors, Actions as RootActions } from 'store/root-reducer';
import { Actions, ofType } from '@ngrx/effects';

import { CustomerActionTypes } from 'store/actions/customer.actions';
import { RecordActionTypes } from 'store/actions/record.action';
import { SearchActionTypes } from 'store/actions/search.actions';

import { Customer } from 'modules/shared/models/customer.models';
import * as CustomerActions from 'store/actions/customer.actions';
import { Crp } from 'modules/shared/models/crp.models';
import { RecordType } from 'modules/shared/models/record.models';
import { IndividualRecord, CorporateRecord } from 'modules/shared/models/record.models';
import { RecordOtherDataResponse } from 'store/models/record.models';
import { CreateOrUpdateCrpPayload, CreateOrUpdateCrpRequest } from 'store/models/customer.models';
import { GetCrpPayload } from 'store/models/crp.models';
import { PostScreeningRequest } from 'store/models/screening.models';
import { CheckBalancePayload } from 'store/models/billable.models';

import { IBreadcrumbs } from 'modules/shared/components/breadcrumbs/breadcrumbs.component';

import { CrpRoleComponent } from 'modules/customers/components/crp-role/crp-role.component';
import { CrpListCardComponent } from 'modules/customers/components/crp-list-card/crp-list-card.component';

import { CrpService } from 'modules/core/services/crp.service';
import { SnackbarService } from 'app/services/snackbar.service';

import { CustomersBase } from '../../customers-base';


export interface ICrpDataForDetailsForm {
  record: IndividualRecord | CorporateRecord;
  customer: Customer;
  crpStatus: string;
}

@Component({
  selector: 'artemis-crp-edit',
  templateUrl: './crp-edit.page.html',
  styleUrls: ['./crp-edit.page.scss']
})
export class CrpEditPage extends CustomersBase implements OnInit, OnDestroy {
  private unsubscribe$: Subject<any> = new Subject();
  crpData: ICrpDataForDetailsForm;
  currentCrp: Crp;
  individualFormObject: FormGroup;
  corporateFormObject: FormGroup;
  individualForm: any;
  corporateForm: any;
  customerType: any;
  individualFormValid: boolean = false;
  corporateFormValid: boolean = false;

  triedSubmit: boolean = false;

  breadcrumbs: Array<IBreadcrumbs> = [];

  loading$: Observable<boolean>;
  formLoading: boolean = false;

  otherData: RecordOtherDataResponse;

  @ViewChild(CrpRoleComponent, { static: false }) crpRoleComponent: CrpRoleComponent;
  @ViewChild(CrpListCardComponent, { static: true }) crpListCard: CrpListCardComponent;
  @ViewChild('customerDocument', { static: true }) customerDocument: ElementRef;
  @ViewChild('detailForm', { static: true }) detailForm: ElementRef;

  constructor(private cdRef: ChangeDetectorRef,
              private store: Store<AppState>,
              private router: Router,
              private activatedRoute: ActivatedRoute,
              private crpService: CrpService,
              private snackBar: SnackbarService,
              private actions$: Actions) {
    super();
  }

  ngOnInit() {
    this.getLoadingStatus();
    if (this.unsubscribe$) {
      this.unsubscribe$.next();
      this.unsubscribe$.complete();
      this.unsubscribe$ = new Subject();
    }

    combineLatest([
      merge(
        this.store.select(Selectors.record.getCorporateRecord),
        this.store.select(Selectors.record.getIndividualRecord)
      ),
      this.store.select(Selectors.customer.getCustomer),
      this.store.select(Selectors.customer.getCurrentCrp)
    ]).pipe(
      filter(([record, customer, currentCrp]: [IndividualRecord | CorporateRecord, Customer, Crp]) => {
        return !!record && !!customer && !!currentCrp;
      }),
      takeUntil(this.unsubscribe$)
    ).subscribe(
      ([record, customer, currentCrp]: [IndividualRecord | CorporateRecord, Customer, Crp]) => {
        this.currentCrp = currentCrp;
        this.crpData = <ICrpDataForDetailsForm>{
          record: record,
          customer: customer,
          crpStatus: currentCrp.status
        };

        this.loadBreadcrumbs(customer.id, customer.name, record.id, record.name);
        this.cdRef.detectChanges();
      });

    this.activatedRoute.params.subscribe(params => {
      this.detailForm.nativeElement.scrollIntoView({ behavior: 'smooth' });
      this.store.dispatch(new RootActions.customer.GetCrp(<GetCrpPayload>{
        crpId: params['crpId'],
        customerId: params['id'],
        getOtherData: true
      }));
    });

    this.store.select(Selectors.record.getRecordOtherData)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res: RecordOtherDataResponse) => {
        if (this.hasValidOtherData(res)) {
          this.otherData = res;
        } else {
          this.otherData = null;
        }
      });
  }

  private getAddCrpSuccessStatus(): void {
    this.actions$.pipe(
      ofType<CustomerActions.CreateOrUpdateCrpSuccess>(CustomerActions.CustomerActionTypes.CREATE_OR_UPDATE_CRP_SUCCESS),
      takeUntil(this.unsubscribe$)
    )
    .subscribe(
      () => {
        this.store.dispatch(new RootActions.customer.GetCustomerStatus({
          customerId: this.currentCrp.customer
        }));
      }
    );
  }

  /**
   * Get loading status from store
   * @method getLoadingStatus
   */
  private getLoadingStatus(): void {
    this.loading$ = combineLatest([
      this.store.select(Selectors.customer.getLoading),
      this.store.select(Selectors.record.getLoading),
      this.store.select(Selectors.search.getLoading)
    ]).pipe(
      takeUntil(this.unsubscribe$),
      map(([
        customerLoading, recordLoading, screeningLoading]:
        [Array<CustomerActionTypes>, Array<RecordActionTypes>, Array<SearchActionTypes>]
      ) => {
        return customerLoading.includes(CustomerActionTypes.GET_CRP) ||
               customerLoading.includes(CustomerActionTypes.UPDATE_AND_RA_CRP) ||
               customerLoading.includes(CustomerActionTypes.UPDATE_AND_ADD_CRP) ||
               customerLoading.includes(CustomerActionTypes.CREATE_OR_UPDATE_CRP) ||
               recordLoading.includes(RecordActionTypes.GET_OTHER_DATA) ||
               recordLoading.includes(RecordActionTypes.GET_INDIVIDUAL_RECORD) ||
               recordLoading.includes(RecordActionTypes.GET_CORPORATE_RECORD) ||
               screeningLoading.includes(SearchActionTypes.POST_SCREENING);
      })
    );
  }

  private loadBreadcrumbs(customerId: number, customerName: string, recordId: number, recordName: string): void {
    this.breadcrumbs = [
      {
        url: '/customers',
        label: 'Customer List'
      }, {
        url: `/customers/${customerId}`,
        label: customerName
      }, {
        url: `/customers/${customerId}/edit/crp-edit/${recordId}`,
        label: `Edit - ${recordName}`
      }
    ];
  }

  // Get current CRP
  getCurrenCRP(): void {
    this.store.dispatch(new RootActions.customer.GetCrp({
      crpId: this.currentCrp.id
    }));
    this.customerDocument.nativeElement.scrollIntoView({ behavior: 'smooth' });
  }

  isFormValid(): boolean {
    return (this.customerType === RecordType.INDIVIDUAL && this.individualFormValid) ||
      (this.customerType === RecordType.CORPORATE && this.corporateFormValid);
  }

  isFormDirty(): boolean {
    return (this.customerType === RecordType.INDIVIDUAL && this.individualFormObject.dirty) ||
      (this.customerType === RecordType.CORPORATE && this.corporateFormObject.dirty);
  }

  /**
   * Check if user has entered any alias name or former registered name
   * @method hasFormerOrAliasName
   */
  hasFormerOrAliasName(): boolean {
    if (this.customerType === RecordType.INDIVIDUAL) {
      return this.individualForm.aliasNames.length || this.individualForm.oldNames.length;
    } else if (this.customerType === RecordType.CORPORATE) {
      return this.corporateForm.aliasNames.length || this.corporateForm.oldNames.length;
    }

    return false;
  }

  updateCustomer(subaction?: string) {
    this.triedSubmit = true;
    if (this.isFormDirty() && this.isFormValid()) {
      let recordPayload;
      let crpDataToSend: CreateOrUpdateCrpRequest;

      if (this.customerType === RecordType.INDIVIDUAL) {
        recordPayload = {
          id: this.crpData.record.id,
          title: this.individualForm.title,
          name: this.individualForm.name,
          aliasNames: this.individualForm.aliasNames,
          oldNames: this.individualForm.oldNames,
          nationality: this.individualForm.nationality,
          countryOfResidence: this.individualForm.countryOfResidence,
          gender: this.individualForm.gender,
          dateOfBirth: this.individualForm.dateOfBirth,
          industry: this.individualForm.industry,
          occupation: this.individualForm.occupation,
          addresses: this.individualForm.addresses,
          phoneNumbers: this.individualForm.phoneNumbers,
          referenceId: this.individualForm.recordReferenceId,
          sourceOfFunds: this.individualForm.sourceOfFunds,
          emailAddresses: this.individualForm.emailAddresses,
          bankAccounts: this.individualForm.bankAccounts,
          countryOfBirth: this.individualForm.countryOfBirth,
          idType: this.individualForm.idType,
          idNumber: this.individualForm.idNumber,
          idIssueDate: this.individualForm.idIssueDate,
          idExpiryDate: this.individualForm.idExpiryDate,
          isUndischargedBankrupt: this.individualForm.isUndischargedBankrupt
        };

        crpDataToSend = <CreateOrUpdateCrpRequest>{
          primary: false,
          status: this.individualForm.status,
          individualRecord: recordPayload
        };
      }

      if (this.customerType === RecordType.CORPORATE) {
        recordPayload = {
          id: this.crpData.record.id,
          isIncorporated: !!this.corporateForm.isIncorporated,
          name: this.corporateForm.name,
          aliasNames: this.corporateForm.aliasNames,
          oldNames: this.corporateForm.oldNames,
          entityType: this.corporateForm.entityType,
          ownershipStructureLayers: this.corporateForm.ownershipStructureLayers,
          countryOfIncorporation: this.corporateForm.countryOfIncorporation,
          countryOfOperations: this.corporateForm.countryOfOperations,
          businessActivity: this.corporateForm.businessActivity,
          addresses: this.corporateForm.addresses,
          phoneNumbers: this.corporateForm.phoneNumbers,
          referenceId: this.corporateForm.recordReferenceId,
          sourceOfFunds: this.corporateForm.sourceOfFunds,
          emailAddresses: this.corporateForm.emailAddresses,
          bankAccounts: this.corporateForm.bankAccounts,
          incorporationNumber: this.corporateForm.incorporationNumber,
          website: this.corporateForm.website,
          incorporationDate: this.corporateForm.incorporationDate,
          incorporationExpiryDate: this.corporateForm.incorporationExpiryDate,
          isUndischargedBankrupt: this.corporateForm.isUndischargedBankrupt,
          imoNumber: this.corporateForm.imoNumber,
        };

        crpDataToSend = <CreateOrUpdateCrpRequest>{
          primary: false,
          status: this.corporateForm.status,
          corporateRecord: recordPayload
        };
      }

      if (subaction === 'ra') {
        this.store.dispatch(new RootActions.billable.CheckBalance(<CheckBalancePayload>{
          actions: [
            new RootActions.customer.UpdateAndRACrp(<CreateOrUpdateCrpPayload>{
              crpId: this.currentCrp.id,
              crpData: crpDataToSend,
              customer: this.crpData.customer,
              mode: 'UPDATE'
            })
          ]
        }));
      } else if (subaction === 'crp') {
        this.store.dispatch(new RootActions.customer.UpdateAndAddCrp(
          <CreateOrUpdateCrpPayload>{
            crpId: this.currentCrp.id,
            crpData: crpDataToSend,
            customer: this.crpData.customer,
            mode: 'UPDATE'
          }
        ));
      } else {
        this.store.dispatch(new RootActions.customer.CreateOrUpdateCrp(
          <CreateOrUpdateCrpPayload>{
            crpId: this.currentCrp.id,
            crpData: crpDataToSend,
            customer: this.crpData.customer,
            mode: 'UPDATE'
          }
        ));
      }
    } else if (this.isFormValid()) {
      if (subaction === 'ra') {
        this.store.dispatch(new RootActions.billable.CheckBalance(<CheckBalancePayload>{
          actions: [
            new RootActions.search.PostScreening(<PostScreeningRequest>{
              generateReport: true,
              invalidOnly: true,
              redirectToScreening: true
            })
          ]
        }));
      } else if (subaction === 'crp') {
        this.router.navigate([`/customers/${this.crpData.customer.id}/edit/crp-add`], { fragment: 'addCrpCard' });
      }
    }

    this.getAddCrpSuccessStatus();
  }

  formChange(data: any) {
    this.individualForm = data.individualForm;
    this.corporateForm = data.corporateForm;
    this.customerType = data.customerType;
    this.individualFormValid = data.individualFormValid;
    this.corporateFormValid = data.corporateFormValid;
    this.corporateFormObject = data.corporateFormObject;
    this.individualFormObject = data.individualFormObject;
  }

  onRoleChange(data: any) {
    if (data.type === 'ADD') {
      this.crpService.addCrpRole(data.role).subscribe(() => {
        this.crpRoleComponent.reload();
        this.crpListCard.reloadCrps();
      }, error => this.snackBar.error(error.error.detail));
    } else if (data.type === 'EDIT') {
      this.crpService.updateCrpRole(data.role).subscribe(() => {
        this.crpRoleComponent.reload();
        this.crpListCard.reloadCrps();
      }, error => this.snackBar.error(error.error.detail));
    } else if (data.type === 'DELETE') {
      this.crpService.deleteRole(data.role.id).subscribe(() => {
        this.crpRoleComponent.reload();
        this.crpListCard.reloadCrps();
      }, error => this.snackBar.error(error.error.detail));
    }
  }

  /**
   * update form loading status
   * @method formLoadingHandler
   */
  formLoadingHandler(formLoading: boolean): void {
    this.formLoading = formLoading;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.store.dispatch(new RootActions.record.ResetRecord());
    this.store.dispatch(new RootActions.customer.ResetCustomerOverview());
  }
}
